import React, { memo, useEffect, useState } from "react"

import { graphql, Link, useStaticQuery } from "gatsby"

import { useKeenSlider } from "keen-slider/react"

import AutoplayPlugin from "./autoplay-plugin"
import {
  ClickCollectIcon,
  ExperienceIcon,
  StockIcon,
  VerifiedRatingIcon,
} from "./usp-icons"

import * as styles from "./usp-bar.module.css"

const CATALOGUE_URL =
  "https://media.firstfence.co.uk/docs/AW00966_First+Fence_Web+Catalogue.pdf"

/* Below this width the USPs rotate as a carousel; at or above it they sit in a
   static row. */
const CAROUSEL_QUERY = "(max-width: 1023px)"

/* The winning UVP test was desktop only, so below CAROUSEL_QUERY the bar is
   hidden. Flip to true to bring back the mobile carousel. */
const SHOW_ON_MOBILE = false

/* Shown if the Feefo summary node is missing (the type is registered in
   gatsby-node, so a failed fetch resolves to null rather than breaking). */
const FALLBACK_RATING = 4.7

const buildUsps = (rating) => [
  {
    id: 1,
    Icon: VerifiedRatingIcon,
    text: `${rating.toFixed(1)}/5 Verified Rating`,
    to: "/testimonials",
  },
  {
    id: 2,
    Icon: ClickCollectIcon,
    text: "Free Click & Collect",
    to: "/depot-locator",
  },
  {
    id: 3,
    Icon: ExperienceIcon,
    text: "16+ Years Experience",
    to: "/about-first-fence",
  },
  {
    id: 4,
    Icon: StockIcon,
    text: "Large UK Stock Availability",
    href: CATALOGUE_URL,
  },
]

/* The catalogue lives on the media subdomain and can't be client-routed, so it
   needs a plain anchor while the rest use Gatsby Link. */
const UspLink = ({ usp }) => {
  const { Icon, text } = usp
  const body = (
    <>
      <Icon className={styles.icon} />
      <span>{text}</span>
    </>
  )

  if (usp.href) {
    return (
      <a
        href={usp.href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        {body}
      </a>
    )
  }

  return (
    <Link to={usp.to} className={styles.link}>
      {body}
    </Link>
  )
}

const USPBar = memo(() => {
  const data = useStaticQuery(graphql`
    query USPBarRatingQuery {
      feefoReviewSummary {
        ratingAverage
      }
    }
  `)

  const [isCarousel, setIsCarousel] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  /* The header is server-rendered and viewport width isn't knowable at build
     time, so SSR emits the row and the carousel takes over after mount. */
  useEffect(() => {
    const carousel = window.matchMedia(CAROUSEL_QUERY)
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)")

    const sync = () => {
      setIsCarousel(SHOW_ON_MOBILE && carousel.matches)
      setReducedMotion(motion.matches)
    }

    sync()
    carousel.addEventListener("change", sync)
    motion.addEventListener("change", sync)

    return () => {
      carousel.removeEventListener("change", sync)
      motion.removeEventListener("change", sync)
    }
  }, [])

  const usps = buildUsps(
    data?.feefoReviewSummary?.ratingAverage ?? FALLBACK_RATING
  )

  const [sliderRef] = useKeenSlider(
    {
      loop: true,
      slides: { perView: 1 },
    },
    reducedMotion ? [] : [AutoplayPlugin(5000)]
  )

  if (!isCarousel) {
    return (
      <div
        className={
          SHOW_ON_MOBILE
            ? styles.content
            : `${styles.content} ${styles.desktopOnly}`
        }
      >
        <div className={styles.row}>
          {usps.map((usp) => (
            <div className={styles.item} key={usp.id}>
              <UspLink usp={usp} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.content}>
      <div ref={sliderRef} className="keen-slider">
        {usps.map((usp) => (
          <div className={`keen-slider__slide ${styles.slide}`} key={usp.id}>
            <UspLink usp={usp} />
          </div>
        ))}
      </div>
    </div>
  )
})

export default USPBar
